// Banner Rental Payment Component
import React, { useState, useEffect } from 'react';
import { useCampaign } from '../../context/CampaignContext';
import { useTCoins, TCOINS_TYPES } from '../../context/TCoinsContext';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { PAYMENT_METHODS, PAYMENT_STATUS } from '../../data/campaignModels';
import { Timestamp } from 'firebase/firestore';

const BannerRentalPayment = ({ campaign, onPaymentSuccess, onCancel }) => {
  const { updateCampaign } = useCampaign();
  const { deductTCoins, tcoins, loadTCoins } = useTCoins();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.T_COINS);
  const [paymentAmount, setPaymentAmount] = useState(campaign?.rental?.paymentAmount || 0);

  useEffect(() => {
    if (campaign?.rental?.customerId) {
      loadCustomer();
    }
  }, [campaign]);

  useEffect(() => {
    if (currentUser) {
      loadTCoins(currentUser.uid);
    }
  }, [currentUser]);

  const loadCustomer = async () => {
    try {
      const userRef = doc(db, 'users', campaign.rental.customerId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setCustomer({ id: userSnap.id, ...userSnap.data() });
      }
    } catch (error) {
      console.error('Error loading customer:', error);
    }
  };

  const handlePayment = async () => {
    if (!campaign || !customer) {
      alert('ไม่พบข้อมูลลูกค้า');
      return;
    }

    if (!window.confirm(`ยืนยันการหัก ${paymentAmount} ${paymentMethod === PAYMENT_METHODS.T_COINS ? 'T Coins' : 'Bonus Coins'} จากลูกค้า?`)) {
      return;
    }

    setLoading(true);
    try {
      const coinType = paymentMethod === PAYMENT_METHODS.T_COINS ? TCOINS_TYPES.REAL : TCOINS_TYPES.VIRTUAL;
      
      // Check balance
      const customerBalance = customer.tcoins?.[coinType] || 0;
      if (customerBalance < paymentAmount) {
        alert(`ลูกค้ามี ${coinType === TCOINS_TYPES.REAL ? 'T Coins' : 'Bonus Coins'} ไม่เพียงพอ (มี: ${customerBalance}, ต้องการ: ${paymentAmount})`);
        setLoading(false);
        return;
      }

      // Deduct T Coins
      const success = await deductTCoins(
        customer.id,
        coinType,
        paymentAmount,
        `Banner Rental Payment - Campaign: ${campaign.name?.th || campaign.name?.en || campaign.id}`
      );

      if (!success) {
        alert('เกิดข้อผิดพลาดในการหัก T Coins');
        setLoading(false);
        return;
      }

      // Update campaign payment status
      const transactionId = `banner-rental-${campaign.id}-${Date.now()}`;
      await updateCampaign(campaign.id, {
        'rental.paymentStatus': PAYMENT_STATUS.PAID,
        'rental.paidAt': Timestamp.now(),
        'rental.transactionId': transactionId,
        'rental.paymentMethod': paymentMethod,
        'rental.paymentAmount': paymentAmount,
        status: 'pending-approval' // Move to approval after payment
      });

      alert('ชำระเงินสำเร็จ! Campaign ถูกย้ายไปรออนุมัติ');
      if (onPaymentSuccess) onPaymentSuccess();
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!campaign || !campaign.rental) {
    return (
      <div className="p-4 text-center text-gray-500">
        ไม่พบข้อมูล Banner Rental
      </div>
    );
  }

  const customerBalance = customer?.tcoins?.[paymentMethod === PAYMENT_METHODS.T_COINS ? TCOINS_TYPES.REAL : TCOINS_TYPES.VIRTUAL] || 0;
  const hasEnoughBalance = customerBalance >= paymentAmount;

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl">
      <h2 className="text-xl font-bold mb-4">ชำระเงิน Banner Rental</h2>
      
      <div className="space-y-4 mb-6">
        <div>
          <div className="text-sm text-gray-600 mb-1">Campaign:</div>
          <div className="font-medium">{campaign.name?.th || campaign.name?.en || 'ไม่มีชื่อ'}</div>
        </div>

        {customer && (
          <div>
            <div className="text-sm text-gray-600 mb-1">ลูกค้า:</div>
            <div className="font-medium">{customer.displayName || customer.email || customer.id}</div>
          </div>
        )}

        <div>
          <div className="text-sm text-gray-600 mb-1">จำนวนที่ต้องจ่าย:</div>
          <div className="text-2xl font-bold text-green-600">
            {paymentAmount.toLocaleString()} {paymentMethod === PAYMENT_METHODS.T_COINS ? 'T Coins' : 'Bonus Coins'}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">วิธีชำระเงิน:</label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={PAYMENT_METHODS.T_COINS}>T Coins</option>
            <option value={PAYMENT_METHODS.BONUS_COINS}>Bonus Coins</option>
          </select>
        </div>

        {customer && (
          <div>
            <div className="text-sm text-gray-600 mb-1">ยอดคงเหลือ:</div>
            <div className={`font-medium ${hasEnoughBalance ? 'text-green-600' : 'text-red-600'}`}>
              {customerBalance.toLocaleString()} {paymentMethod === PAYMENT_METHODS.T_COINS ? 'T Coins' : 'Bonus Coins'}
            </div>
            {!hasEnoughBalance && (
              <div className="text-xs text-red-600 mt-1">
                ยอดคงเหลือไม่เพียงพอ (ต้องการ: {paymentAmount.toLocaleString()})
              </div>
            )}
          </div>
        )}

        {campaign.rental.pricing && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-2">รายละเอียดราคา:</div>
            <div className="text-sm">
              <div>ประเภท: {campaign.rental.pricing.type === 'daily' ? 'รายวัน' : campaign.rental.pricing.type === 'weekly' ? 'รายสัปดาห์' : 'รายเดือน'}</div>
              <div>อัตรา: {campaign.rental.pricing.rate.toLocaleString()} / หน่วย</div>
              <div>จำนวนวัน: {campaign.rental.pricing.totalDays} วัน</div>
              <div>ราคารวม: {campaign.rental.pricing.totalAmount.toLocaleString()}</div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          ยกเลิก
        </button>
        <button
          onClick={handlePayment}
          disabled={loading || !hasEnoughBalance || campaign.rental.paymentStatus === PAYMENT_STATUS.PAID}
          className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
        >
          {loading ? 'กำลังดำเนินการ...' : campaign.rental.paymentStatus === PAYMENT_STATUS.PAID ? 'ชำระแล้ว' : 'ชำระเงิน'}
        </button>
      </div>
    </div>
  );
};

export default BannerRentalPayment;

